/**
 * @file: InspectionInfo.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

/* eslint-disable @typescript-eslint/no-use-before-define */
import { LinkButton } from '@euler/app/components/LinkButton';
import { StatusLabel } from '@euler/app/components/StatusLabel';
import { InspectionIcon } from '@euler/app/components/icons/InspectionIcon';
import { Typography } from '@euler/app/components/typography';
import { MemberChatButton } from '@euler/app/flows/chat/components/MemberChatButton';
import type { TaskNavParams } from '@euler/app/flows/task/TaskFlow';
import { flowStatusToCommonTaskStatus } from '@euler/app/flows/task/functions';
import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import { useNavigateToTaskDetail } from '@euler/app/flows/task/functions/useNavigateToTaskDetail';
import { useTaskExtraInfo } from '@euler/app/flows/task/functions/useTaskExtraInfo';
import { useViewReportWithTaskContext } from '@euler/app/flows/task/functions/useViewReportWithTaskContext';
import { TaskCardTitle } from '@euler/app/flows/task/pages/detail/components/TaskCardTitle';
import { Avatar } from '@euler/components/Avatar';
import { Icon } from '@euler/components/Icomoon';
import { TableView } from '@euler/components/TableView';
import { Img } from '@euler/components/image/AdvancedImage';
import { useAppLoading } from '@euler/components/loading/Provider';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { VehicleInspectionFlow } from '@euler/model';
import {
  CommonTaskStatus,
  InspectionCategory,
  InspectionTaskStatus,
} from '@euler/model/enum';
import { SafeHaptics, delayed, withConfirmation } from '@euler/utils';
import { sorted } from '@euler/utils/array';
import { dateFromValue } from '@euler/utils/datetime';
import { timeUtilNow } from '@euler/utils/formatters';
import { usePersistFn } from '@euler/utils/hooks';
import { useActionSheet } from '@expo/react-native-action-sheet';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import compareAsc from 'date-fns/compareAsc';
import { ImpactFeedbackStyle } from 'expo-haptics';
import { memo, useCallback, useMemo } from 'react';
import { View } from 'react-native';
import { useTheme } from 'styled-components';
import { TaskDetailSection } from './TaskDetailSection';
import { TimeElapsedLabel } from './TimeElapsedLabel';

export const WiredInspectionInfo = memo(() => {
  const { taskManager, detail } = useTaskContext();
  const extraInfo = useTaskExtraInfo();
  const { inspection } = detail;
  const appLoading = useAppLoading();
  const navigation = useNavigation<StackNavigationProp<TaskNavParams>>();
  const navigateToTaskDetail = useNavigateToTaskDetail();

  const { showActionSheetWithOptions } = useActionSheet();

  const onNewFlowPress = useCallback(
    (category: InspectionCategory) => {
      navigation.push('inspectionFlows', {
        category,
      });
    },
    [navigation],
  );

  const onCancelFlow = usePersistFn(async (flowId: string) => {
    navigateToTaskDetail();

    delayed(async () => {
      await appLoading.showWithAction(async () => {
        await taskManager.inspectionManager.cancelInspectionFlow(flowId);
        delayed(() => {
          appLoading.show({
            type: 'success',
            message: '已取消',
          });
        }, 350);
      });
    }, 350);
  });

  const viewReport = useViewReportWithTaskContext();

  const onFlowPress = usePersistFn(async (flow: VehicleInspectionFlow) => {
    if (flow.status === 'finished') {
      viewReport('inspectionReport', {
        flowId: flow.id,
        onCancel: onCancelFlow,
      });
      return;
    }

    if (flow.status === 'pending') {
      await appLoading.showWithAction(async () => {
        await taskManager.sync();
        await taskManager.inspectionManager.beginInspectionFlow(flow.id);
      });
    }

    navigation.push('inspection', { flowId: flow.id, onCancel: onCancelFlow });
  });

  const onFlowLongPress = usePersistFn(async (flow: VehicleInspectionFlow) => {
    SafeHaptics.impact(ImpactFeedbackStyle.Medium);
    if (flow.status === 'pending' || flow.status === 'inprogress') {
      showActionSheetWithOptions(
        {
          destructiveButtonIndex: 0,
          cancelButtonIndex: 1,
          options: ['取消该检测任务', '关闭'],
        },
        async index => {
          if (index === 0) {
            if (
              await withConfirmation({
                title: '确认',
                message: `您即将取消检测任务"${flow.name}"，该操作不可撤销，是否确认继续？`,
              })
            ) {
              await appLoading.showWithAction(async () => {
                await taskManager.inspectionManager.cancelInspectionFlow(
                  flow.id,
                );
                delayed(() => {
                  appLoading.show({
                    type: 'success',
                    message: '已取消',
                  });
                }, 350);
              });
            }
          }
        },
      );
    }
  });

  const onFinish = usePersistFn(async () => {
    await taskManager.inspectionManager.finishInspections({
      technicianId: taskManager.currentUser.user.memberId,
      technicianName: taskManager.currentUser.user.name,
    });
  });

  const onViewInspectionReport = usePersistFn(() => {
    viewReport('inspectionReport', { onFinish });
  });

  if (
    detail.basicInfo.status === InspectionTaskStatus.Finished &&
    inspection.status !== CommonTaskStatus.Finished
  ) {
    return null;
  }

  return (
    <TaskDetailSection
      title={
        <TaskCardTitle
          text="车辆检测"
          status={inspection.status}
          shared={extraInfo?.reportShareSummary?.inspection}
          viewed={extraInfo?.reportViewSummary?.inspection}
        />
      }
      headerRight={
        inspection.status === CommonTaskStatus.Finished
          ? '查看报告'
          : inspection.flows.length > 0 &&
            inspection.flows.some(x => x.status !== 'pending')
          ? '预览报告'
          : undefined
      }
      headerRightArrow
      onHeaderRightPress={
        inspection.flows.some(x => x.status !== 'pending')
          ? onViewInspectionReport
          : undefined
      }
      bodyStyle={{
        paddingLeft: 0,
        paddingRight: 0,
        paddingTop: 0,
        paddingBottom: 0,
      }}
    >
      <FlowList
        canAdd={
          detail.basicInfo.status !== InspectionTaskStatus.Finished &&
          detail.basicInfo.status !== InspectionTaskStatus.Cancelled
        }
        inspectionFlows={inspection.flows}
        onNewFlowPress={onNewFlowPress}
        onFlowPress={onFlowPress}
        onFlowLongPress={onFlowLongPress}
      />
    </TaskDetailSection>
  );
});

const FlowList = memo(
  ({
    canAdd,
    inspectionFlows,
    onFlowPress,
    onNewFlowPress,
    onFlowLongPress,
  }: {
    canAdd?: boolean;
    inspectionFlows: VehicleInspectionFlow[];
    onNewFlowPress?: (category: InspectionCategory) => void;
    onFlowPress?: (flow: VehicleInspectionFlow) => void;
    onFlowLongPress?: (flow: VehicleInspectionFlow) => void;
  }) => {
    const theme = useTheme();
    const { rpx } = useResponsiveContext();

    const onNewFlow = useCallback(() => {
      onNewFlowPress?.(InspectionCategory.Normal);
    }, [onNewFlowPress]);

    const flows = useMemo(
      () =>
        sorted(inspectionFlows, (x, y) =>
          compareAsc(dateFromValue(x.createdAt), dateFromValue(y.createdAt)),
        ),
      [inspectionFlows],
    );

    return (
      <TableView debugTag="inspection-info">
        {flows.map(flow => (
          <TableView.Item
            key={flow.id}
            title={flow.name}
            titleStyle={{ marginBottom: rpx(5) }}
            titleTextStyle={{ fontSize: rpx(17), lineHeight: rpx(20) }}
            note={
              <View>
                <Typography.Label
                  size={rpx(13)}
                  lineHeight={rpx(18)}
                  color={p => p.components.table.item.noteColor}
                  numberOfLines={1}
                  light
                >
                  {flow.description ?? '暂无该检测任务的说明'}
                </Typography.Label>
                <View
                  css={`
                    margin-top: ${rpx(4)}px;
                    flex-direction: row;
                    align-items: center;
                  `}
                >
                  <Avatar
                    name={flow.assignedTo.name}
                    css={`
                      width: ${rpx(16)}px;
                      height: ${rpx(16)}px;
                      border-radius: ${rpx(8)}px;
                      margin-right: ${rpx(3)}px;
                    `}
                  />
                  <Typography.Label
                    size={rpx(12)}
                    regular
                    color={p => p.components.table.item.noteColor}
                  >
                    {`${flow.assignedTo.name}`}
                  </Typography.Label>
                  <MemberChatButton
                    orgMemberId={flow.assignedTo.id}
                    size={rpx(14)}
                    style={{ marginLeft: rpx(4) }}
                  />
                  <Typography.Label
                    size={rpx(12)}
                    color={p => p.components.table.item.noteColor}
                    opacity={0.5}
                    ml={rpx(5)}
                    light
                  >
                    {`${timeUtilNow(flow.createdAt)}`}
                  </Typography.Label>
                </View>
              </View>
            }
            noteStyle={{ fontSize: rpx(12) }}
            iconStyle={{ marginRight: rpx(10) }}
            detailIcon="disclosure"
            contentContainerStyle={{
              paddingTop: rpx(15),
              paddingBottom: rpx(15),
            }}
            icon={
              flow.template.icon ? (
                <Img
                  uri={flow.template.icon}
                  css={`
                    width: ${rpx(32)}px;
                    height: ${rpx(32)}px;
                    border-radius: ${rpx(8)}px;
                  `}
                />
              ) : (
                <InspectionIcon size={rpx(32)} />
              )
            }
            detail={
              <View
                css={`
                  align-items: flex-end;
                  justify-content: center;
                  margin-left: ${rpx(15)}px;
                `}
              >
                <StatusLabel
                  status={flowStatusToCommonTaskStatus(flow.status)}
                  continuable
                />
                <TimeElapsedLabel flow={flow} />
              </View>
            }
            onPress={onFlowPress?.bind(null, flow)}
            onLongPress={onFlowLongPress?.bind(null, flow)}
          />
        ))}
        {flows.length === 0 && canAdd ? (
          <TableView.Item onPress={onNewFlow}>
            <Typography.Label
              light
              color={p => p.form.label.color}
              size={rpx(14)}
            >
              检测未开始，点击添加检测任务
            </Typography.Label>
          </TableView.Item>
        ) : null}
        {canAdd ? (
          <TableView.Item
            contentContainerStyle={{
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
            }}
            onPress={onNewFlow}
          >
            <LinkButton
              text="添加检测任务"
              onPress={onNewFlow}
              textStyle={{
                lineHeight: rpx(18),
              }}
            />
            <Icon
              name="add-circle-outline"
              size={rpx(18)}
              color={theme.link}
              css={`
                margin-left: ${rpx(5)}px;
              `}
            />
          </TableView.Item>
        ) : null}
      </TableView>
    );
  },
);
